import { Stack, styled, Typography } from '@mui/material';

import { BillingChannel, EmulatorType, PlanFamily, Subscription, SubscriptionStatus } from '@localstack/types';
import { EmulatorPills, getPeriodEnd, SubscriptionStatusWithCancelling } from '@localstack/ui';
import { formatBillingCadence, formatDateTo, formatMonetaryAmount } from '@localstack/services';

import { ReactNode } from 'react';
import { Report as ReportIcon } from '@mui/icons-material';

const CadenceLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  textTransform: 'capitalize',
}));

const WarningText = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const getDescription = (status: SubscriptionStatusWithCancelling, subscription: Subscription): ReactNode => {
  if (status === SubscriptionStatus.TRIALING) {
    return 'Free during the trial. Unlimited seats.';
  }

  if (status === SubscriptionStatus.INCOMPLETE) {
    return (
      <WarningText>
        <ReportIcon color="error" fontSize="small" />
        Payment failed. Retry payment below to restore access.
      </WarningText>
    );
  }

  if (status === SubscriptionStatus.UNPAID) {
    return (
      <WarningText>
        <ReportIcon color="error" fontSize="small" sx={{ flexShrink: 0 }} />
        <span>
          Your subscription is inactive due to an unpaid invoice. To reactivate your subscription, please contact{' '}
          <b>accounts-receivable@localstack.cloud</b>
        </span>
      </WarningText>
    );
  }

  if (PlanFamily.PREVIEW_PLANS === subscription.plan?.family) {
    return 'Free during the public preview. Unlimited seats.';
  }

  if (PlanFamily.FREEMIUM_PLANS === subscription.plan?.family) {
    return 'For non-commercial use only. Upgrade for commercial use.';
  }

  if (PlanFamily.HOBBY_PLANS === subscription.plan?.family) {
    return '55+ AWS services for free. Upgrade for more services and more.';
  }

  if (subscription.billing_channel === BillingChannel.AWS_MARKETPLACE) {
    return 'Manage this subscription in the AWS Marketplace';
  }

  const periodEndDate = formatDateTo(getPeriodEnd(subscription), 'MMM DD, YYYY');

  if (status === 'cancelling') {
    const cancelDate = subscription.cancel_at_period_end ? getPeriodEnd(subscription) : subscription.cancel_at;
    return `Scheduled to cancel on ${formatDateTo(cancelDate ?? getPeriodEnd(subscription), 'MMM DD, YYYY')}`;
  }

  const renewalPrice = formatMonetaryAmount(
    (subscription.estimated_mrr ?? 0) * subscription.interval_months,
    subscription.currency ?? 'usd',
  );

  return subscription.custom_renewal_date
    ? `Renews at: ${periodEndDate}`
    : `Next invoice: ${renewalPrice} on ${periodEndDate}`;
};

/** First column of the subscription content, containing title, emulator type and some description */
export const SubscriptionOverviewColumn = ({
  subscription,
  status,
  emulators,
}: {
  subscription: Subscription;
  status: SubscriptionStatusWithCancelling;
  /** The emulators this subscription gives access to. */
  emulators: EmulatorType[];
}): JSX.Element => (
  <Stack gap="2px">
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
      data-testid={`plan-${subscription.plan.family}`}
    >
      <Typography color="text.primary" fontWeight={500}>
        {subscription.name}
      </Typography>
      {subscription.plan.multi_interval_plan_family && (
        <CadenceLabel>({formatBillingCadence(subscription.interval_months)})</CadenceLabel>
      )}
      <EmulatorPills emulators={emulators} />
    </span>
    <Typography color="text.tertiary">{getDescription(status, subscription)}</Typography>
  </Stack>
);
